import { App as AntApp, ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import enUS from 'antd/locale/en_US'
import zhCN from 'antd/locale/zh_CN'
import zhHK from 'antd/locale/zh_HK'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { Layout } from '@/components/Layout'
import { AppRoutes } from '@/routes'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'
import { setMessageInstance, setNotificationInstance } from '@/utils/notification'

// Map i18n language to antd locale
const antdLocales: Record<string, Locale> = {
  'en-US': enUS,
  'zh-CN': zhCN,
  'zh-HK': zhHK,
}

// Provider to set up global notification/message instances
function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { notification, message } = AntApp.useApp()

  useEffect(() => {
    setNotificationInstance(notification)
    setMessageInstance(message)
  }, [notification, message])

  return <>{children}</>
}

function AppContent() {
  const location = useLocation()
  const isLoginPage = location.pathname === '/login'
  const { isAuthenticated } = useAuthStore()
  const { fetchSettings, initialized, getPrimaryColor } = useSettingsStore()
  const { i18n } = useTranslation()

  // Get current antd locale based on i18n language
  const antdLocale = antdLocales[i18n.language] || enUS

  // Fetch settings on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && !initialized) {
      fetchSettings()
    }
  }, [isAuthenticated, initialized, fetchSettings])

  const primaryColor = getPrimaryColor()

  if (isLoginPage) {
    return (
      <ConfigProvider
        locale={antdLocale}
        theme={{
          token: {
            colorPrimary: primaryColor,
            borderRadius: 6,
            colorBorderSecondary: '#e8e8e8',
            colorSplit: '#e8e8e8',
          },
        }}
      >
        <AntApp>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </AntApp>
      </ConfigProvider>
    )
  }

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 6,
          colorBorderSecondary: '#e8e8e8',
          colorSplit: '#e8e8e8',
        },
      }}
    >
      <AntApp>
        <NotificationProvider>
          <Layout>
            <AppRoutes />
          </Layout>
        </NotificationProvider>
      </AntApp>
    </ConfigProvider>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  )
}

export default App
